import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useQuotas, useUpsertQuota } from '../../hooks';
import { Save, X, Plus } from 'lucide-react';
import { useToast } from '../../components/ui/toast';

interface QuotaRow {
  id: string;
  emp_code: string;
  year: number;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
}

type FormData = {
  emp_code: string; year: number;
  sick_quota: number; personal_quota: number; vacation_quota: number;
};

function EditModal({ quota, onClose }: { quota?: QuotaRow; onClose: () => void }) {
  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: quota
      ? { ...quota }
      : { year: new Date().getFullYear(), sick_quota: 30, personal_quota: 3, vacation_quota: 6 },
  });
  const upsert = useUpsertQuota();
  const toast = useToast();

  async function onSubmit(data: FormData) {
    try {
      await upsert.mutateAsync({
        ...data,
        year: Number(data.year),
        sick_quota: Number(data.sick_quota),
        personal_quota: Number(data.personal_quota),
        vacation_quota: Number(data.vacation_quota),
      } as Record<string, unknown>);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">{quota ? 'แก้ไข' : 'เพิ่ม'}สิทธิ์การลา</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="label">รหัสพนักงาน *</label>
            <input {...register('emp_code', { required: true })} className="input" readOnly={!!quota} />
          </div>
          <div>
            <label className="label">ปี (YYYY) *</label>
            <input type="number" {...register('year', { required: true })} className="input" readOnly={!!quota} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="label">ป่วย (วัน)</label>
              <input type="number" step="0.5" {...register('sick_quota', { required: true, min: 0 })} className="input" />
            </div>
            <div>
              <label className="label">กิจส่วนตัว</label>
              <input type="number" step="0.5" {...register('personal_quota', { required: true, min: 0 })} className="input" />
            </div>
            <div>
              <label className="label">พักร้อน</label>
              <input type="number" step="0.5" {...register('vacation_quota', { required: true, min: 0 })} className="input" />
            </div>
          </div>
          <button type="submit" disabled={upsert.isPending} className="btn-primary w-full">
            <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function HRQuotas() {
  const { data, isLoading } = useQuotas() as { data: QuotaRow[] | undefined; isLoading: boolean };
  const [editing, setEditing] = useState<QuotaRow | null | 'new'>(null);
  const currentYear = new Date().getFullYear();
  const filtered = (data ?? []).filter((q) => q.year === currentYear);

  if (isLoading) return <AdminLayout title="สิทธิ์การลา"><p className="py-8 text-center text-gray-500">กำลังโหลด...</p></AdminLayout>;

  return (
    <AdminLayout title="สิทธิ์การลา">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">ปี {currentYear}</p>
        <button onClick={() => setEditing('new')} className="btn-primary btn-slim"><Plus className="w-3.5 h-3.5" /> เพิ่ม</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">พนักงาน</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">ป่วย</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">กิจ</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">พักร้อน</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((q) => (
              <tr key={q.id}>
                <td className="px-3 py-2 font-mono text-xs">{q.emp_code}</td>
                <td className="px-3 py-2 text-right">{q.sick_quota}</td>
                <td className="px-3 py-2 text-right">{q.personal_quota}</td>
                <td className="px-3 py-2 text-right">{q.vacation_quota}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => setEditing(q)} className="text-xs text-brand-600 underline">แก้ไข</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-gray-500 py-6">ยังไม่มีข้อมูล</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing !== null && (
        <EditModal
          quota={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </AdminLayout>
  );
}
